import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Container, Grid, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Logo from "src/components/Logo";
import Page from "src/components/Page";
import axiosInstance from "src/utils/axios";
import * as yup from "yup";

type FormValuesProps = {
  hash: string;
};

interface ValidateResponse {
  created_at: string;
  success: boolean;
  type: string;
  user: string;
  year: number;
}

const declarationTypes: Record<string, string> = {
  DEBT_CLEARANCE: "Declaração Anual de Quitação de Débitos",
};

const StyledRoot = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "start",
  flexDirection: "column",
  padding: theme.spacing(12, 0),
}));

// Schema de validação com Yup
const schema = yup.object().shape({
  hash: yup.string().required("Código de verificação é obrigatório"),
});

export default function DeclarationValidate() {
  const reCaptchaKEY = process.env.REACT_APP_RECAPTCHA || "";
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");
  const [validateResponse, setValidateResponse] = useState<ValidateResponse | null>(null);

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(schema),
    defaultValues: {
      hash: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    setIsSubmitting(true);
    setValidateResponse(null);

    try {
      const { hash } = data;
      const response = await axiosInstance.post("/users/declaration/validate", {
        hash,
      });

      setValidateResponse(response.data);
    } catch (err) {
      let errorMessage = "Ocorreu um erro ao validar o documento. Por favor, tente novamente.";

      if (err?.statusCode === 404) {
        errorMessage = err?.message || "Código de verificação inválido.";
      }

      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page title="Validação de Documentos | Sempre">
      <StyledRoot>
        <Container maxWidth="lg">
          <StyledContent>
            <Logo />
            <Box sx={{ mt: 5 }}>
              <Typography variant="h4" gutterBottom color="primary">
                Validação de Documentos
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Esta página permite verificar a autenticidade de documentos emitidos pelo Portal do
                Cliente. Para isso, utilize o código de verificação presente no documento. Ao
                informá-lo no campo abaixo, o sistema confirmará se o documento é válido,
                assegurando sua integridade e origem.
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mt: 2 }}>
                Em casos de dúvidas, você também pode entrar em contato com a nossa Central de
                Atendimento pelos telefones (84) 4000-2002 ou 0800 402 2002, ou ainda procurar uma
                de nossas unidades da Sempre Assistência Familiar.
              </Typography>
            </Box>
            <FormProvider {...methods}>
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                autoComplete="off"
                sx={{ mt: 5 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" color="primary">
                        Código de verificação *
                      </Typography>
                      <Controller
                        name="hash"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            value={value}
                            onChange={onChange}
                            error={Boolean(error)}
                            helperText={error?.message}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <ReCAPTCHA
                        sitekey={reCaptchaKEY}
                        ref={recaptchaRef}
                        onChange={(value) => setCaptchaValue(value || "")}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {validateResponse && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="subtitle1">O documento é válido.</Typography>
                    <Typography variant="body2" fontSize={12}>
                      {declarationTypes[validateResponse.type] ||
                        "Tipo de documento não reconhecido"}
                    </Typography>
                    <Typography variant="body2" fontSize={12}>
                      Gerado em{" "}
                      {dayjs(validateResponse.created_at).format("DD/MM/YYYY [às] HH:mm:ss")} para{" "}
                      {validateResponse.user}
                    </Typography>
                    <Typography variant="body2" fontSize={12}>
                      Ano de Referência: {validateResponse.year}
                    </Typography>
                  </Alert>
                )}
                <LoadingButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3 }}
                  loading={isSubmitting}
                  disabled={!captchaValue}
                >
                  Validar
                </LoadingButton>
              </Box>
            </FormProvider>
          </StyledContent>
        </Container>
      </StyledRoot>
    </Page>
  );
}



